import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useSeo } from "../../lib/seo";
import { COMMERCIAL } from "../../lib/labels";

export function ClientLogin() {
  useSeo({ title: "Dossier client", path: "/espace-client", noindex: true });
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/client/me").then(() => nav("/espace-client/dossiers", { replace: true })).catch(() => {}); }, [nav]);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try { setMsg((await api.post("/client/magic-link", { email })).data.message); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <section className="k-section pt-16 max-w-xl">
      <p className="k-eyebrow">Dossier client</p>
      <h1 className="k-h1 mt-4">Retrouver mes demandes.</h1>
      <p className="k-lead mt-5">Indiquez l'email utilisé pour votre demande : nous vous envoyons un lien d'accès personnel, valable 30 minutes. Aucun mot de passe à retenir.</p>
      <form onSubmit={send} className="mt-8 grid gap-4">
        <label htmlFor="cl-email" className="k-label">Email</label>
        <input id="cl-email" type="email" required className="k-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="client-login-email" />
        <button className="k-btn" disabled={busy} data-testid="client-login-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : "Recevoir mon lien d'accès"}</button>
      </form>
      {msg && <p className="mt-6 text-sm border-l-2 border-[#2F7D5B] pl-4" role="status" data-testid="client-login-message">{msg}</p>}
      {err && <p className="mt-6 text-sm text-[#A84C2A]" role="alert">{err}</p>}
      <p className="text-sm text-[#57534E] mt-10">Pas encore de demande ? <Link to="/demande" className="k-link">Démarrer un projet</Link></p>
    </section>
  );
}

export function ClientAccess() {
  useSeo({ title: "Connexion", path: "/espace-client/acces", noindex: true });
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get("token");
    window.history.replaceState(null, "", "/espace-client/acces");
    if (!token) { setErr("Lien incomplet."); return; }
    api.post("/client/session", { token }).then(() => nav("/espace-client/dossiers", { replace: true })).catch((e) => setErr(errMsg(e)));
  }, [params, nav]);
  return (
    <section className="k-section pt-16 max-w-xl" data-testid="client-access">
      {err ? (<><h1 className="k-h2">Accès impossible</h1><p className="k-lead mt-4" role="alert">{err}</p><Link to="/espace-client" className="k-btn mt-8" data-testid="client-access-retry">Demander un nouveau lien</Link></>)
        : <p className="text-sm text-[#8C857B] flex gap-2 items-center"><Loader2 size={16} className="animate-spin" /> Ouverture de votre dossier…</p>}
    </section>
  );
}

export function useClient() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/client/me").then((r) => setMe(r.data)).catch(() => nav("/espace-client", { replace: true })); }, [nav]);
  const logout = async () => { await api.post("/client/logout"); nav("/espace-client"); };
  return { me, logout };
}

export function ClientDossiers() {
  useSeo({ title: "Mes dossiers", path: "/espace-client/dossiers", noindex: true });
  const { me, logout } = useClient();
  const [list, setList] = useState(null);
  useEffect(() => { if (me) api.get("/client/requests").then((r) => setList(r.data)); }, [me]);
  if (!me) return null;
  return (
    <section className="k-section pt-12">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div><p className="k-eyebrow">Dossier client</p><h1 className="k-h1 mt-3">Bonjour {me.name.split(" ")[0]}.</h1></div>
        <div className="flex gap-3"><Link to="/demande" className="k-btn-outline k-btn-sm" data-testid="client-new-request">Nouvelle demande</Link><button onClick={logout} className="k-btn-sm k-btn-outline" data-testid="client-logout">Se déconnecter</button></div>
      </div>
      <div className="mt-10 grid gap-4" data-testid="client-request-list">
        {list?.length === 0 && <p className="text-[#57534E]">Aucune demande pour le moment.</p>}
        {list?.map((r) => (
          <Link key={r.id} to={`/espace-client/dossiers/${r.id}`} className="k-card p-5 sm:p-6 hover:border-[#C25E38] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`client-request-${r.ref}`}>
            <div>
              <p className="font-mono-k text-xs text-[#C25E38]">{r.ref}</p>
              <p className="font-display text-2xl mt-1">{r.offer_label}</p>
              <p className="text-sm text-[#57534E]">Envoyée le {fmtDate(r.created_at)}{r.reorder_of && ` · réassort de ${r.reorder_of}`}</p>
            </div>
            <div className="flex items-center gap-3">
              {r.awaiting_client && <span className="k-badge" style={{ borderColor: "#C25E38", color: "#A84C2A" }}>Votre réponse est attendue</span>}
              <span className="k-badge">{r.status_commercial === "a_completer" ? "Complément demandé" : COMMERCIAL[r.status_commercial]}</span>
              <ArrowRight size={16} />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
